// Rule-based coaching generator for the 3D Feedback Quality engine.
//
// Ported from facilitator_mockup/src/itp_coaching_rule.js (v1.9, 2026-04-23)
// and refreshed 2026-05-12 per AUDIT_RULE_BASED_FALLBACK_2026-05-12.md:
//   #1  unified pickVariation hash (FNV-style + Math.abs) so this server
//       file and the frontend mirror produce identical output for the
//       same input. (The previous frontend hash was additive-without-abs,
//       which could return undefined coaching when the modulo went
//       negative.)
//   #2  Added a ready_to_send branch that returns a positive static
//       message instead of falling through to generic_full (which was
//       coaching strong feedback as if it were weak).
//   #3  Replicated the mockup's variation-key depth (~12 keys with 3-5
//       phrasings each) and multilingual handlers (English, French,
//       Portuguese).
//   #5  Tightened phrasings to land in the 15-30 word band that Phase A
//       AI is now targeting, so user experience is consistent across
//       AI-on and AI-failure paths.
//
// IMPORTANT: this module is mirrored byte-for-byte at
//   frontend/src/features/feedbackFacilitator3d/engine/coaching.js
// Any change here must be applied there too, and vice versa.

/* =====================================================================
 * Coaching variations: each key maps to equivalent phrasings.
 * Design principle: REDIRECT ATTENTION without supplying answers.
 * Use plain language, no jargon. The "what / why / what next" framework
 * maps to COIN (Observation / Impact / Next Steps) without naming it.
 * Never give fill-in-the-blank examples that students might parrot.
 * Phrasings target 15-30 words to match the Phase A AI band.
 * ===================================================================== */
const COACHING_VARIATIONS = {
  too_brief: [
    "Your teammate can't act on this yet. What did they do, why did it matter, and what should they do next?",
    'Name one specific thing they did, why it mattered to the team, and one thing they should keep doing or change.',
    "Peer feedback is most useful when it's specific. What did they do, why did it matter, and what should they do next?",
  ],
  mixed_observation: [
    'To strengthen your feedback, what difference did their actions make, and what should they do next?',
    'Your feedback would be stronger if it also covered why their actions mattered to the team and what they should do going forward. What stands out on each?',
    "Why did what they did matter, and what's one thing they should keep doing or change?",
  ],
  mixed_general: [
    'Why did what they did matter to the team or the project?',
    'What difference did their actions make?',
    'To strengthen your feedback, why did what they did matter?',
  ],
  brief_process: [
    'Good start. When and how did they demonstrate this, and why did it matter?',
    'You have identified a valuable skill. Could you describe a specific instance and why it mattered to the team?',
    'Good observation. When did they show this, and what difference did it make?',
  ],
  trait_full: [
    'What if you focused on what they did rather than who they are? Helpful feedback covers what they did, why it mattered, and what they should do next.',
    'The most helpful feedback describes actions rather than personality. What did they do, why did it matter, and what should they keep doing or change?',
    'What if you shifted from describing who they are to what they did? What actions did they take, why did it matter, and what should they do going forward?',
    'Feedback is most useful when it focuses on actions. What did they do, why did it matter to the team, and what is one thing they should do next?',
    'Rather than describing personality, what did they do, why did it matter, and what should they keep doing or change?',
  ],
  trait_partial: [
    'What if you focused on what they did rather than who they are? What actions did they take, and how did they go about it?',
    'The most helpful feedback describes actions rather than personality. What did this person actually do?',
    'What if you shifted from describing who they are to what they did? What specific actions, rather than traits, come to mind?',
  ],
  generic_full: [
    'Helpful feedback covers three things: what they did, why it mattered, and what they should do next. Which of those could you add?',
    'Strong feedback describes what the person did, why it mattered to the team, and what they should keep doing or change.',
    'What if you built your feedback around: what did they do, why did it matter, and what should they do next?',
    'The most useful feedback covers what they did, why it mattered, and one thing they should keep doing or change.',
    'What did they do, why did it matter to the team, and what should they do going forward?',
  ],
  generic_observation: [
    'Why did their work matter, and what should they do next?',
    'To strengthen this, why did it matter to the team, and what is one thing they should keep doing or change?',
    'What difference did their actions make, and what should they do next?',
    'To round out your feedback, why did their work matter, and what should they do going forward?',
    'Why did it matter to the team, and what is one thing they should keep doing or change?',
  ],
  expand_specifics: [
    'Could you expand on the specifics? How did they go about it, why did it matter, and what should they do next?',
    'Building on the specifics: why did it matter, and what is one thing they should keep doing or change?',
    'Good specifics. Why did it matter to the team, and what should they do going forward?',
  ],
  deepen_task: [
    'What if you thought about not just what they did, but how they went about it?',
    'Your feedback could go deeper. How did they go about the work, not just what came out of it?',
    'To add depth, how did they go about the work, not just what was done?',
  ],
  deepen_task_structure: [
    'What if you thought about not just what they did, but how they went about it, and why it mattered, and what they should do next?',
    'Your feedback could go deeper. How did they go about the work, why did it matter, and what should they do going forward?',
    'To add depth, how did they go about the work, why did it matter to the team, and what should they do next?',
    'How did they go about the work, why did it matter, and what is one thing they should keep doing or change?',
    'How did they do the work, why did it matter to the team, and what should they do going forward?',
  ],
  adequate_observation: [
    'Why did their work matter to the team, and what should they do next?',
    'Good observations. Why did it matter, and what should they do going forward?',
    'Good observations. To make your feedback more complete, why did it matter, and what should they keep doing or change?',
    'Why did their contribution matter, and what should they do going forward?',
    'To round out your feedback, why did their work matter, and what should they do next?',
  ],
  adequate_next_steps: [
    'What is one suggestion you could add: what should they do more of, less of, or differently?',
    'Your feedback would be more useful with a suggestion. What should they keep doing, change, or try next time?',
    'Good feedback. What is one thing they should keep doing or do differently?',
  ],
  critical_only: [
    'What is one strength you could acknowledge before addressing areas for improvement?',
    'Feedback is more effective when it leads with something positive. What is one strength you could note before suggesting improvements?',
    'Starting with a strength makes suggestions more likely to be heard. What did they do well that you could lead with?',
  ],
  positive_only: [
    'Balanced feedback includes both strengths and one area for growth, even a small one. What might that area be?',
    'Positive feedback is a good foundation. What is one area where they could develop further, even if minor?',
    'Helpful feedback pairs recognition with direction. What is one thing they could improve or try differently?',
  ],
  // v6.3 (2026-05-23): mirrored from server. Coaching for closing-line
  // character-judgment phrasings; body of comment is constructive.
  problematic_wrap_up: [
    'Your feedback names specific behaviors, but the closing line edges into character judgment. What if the ending focused on one concrete change they could make instead?',
    'Strong observations in the body. The wrap-up shifts from actions to who they are. Could you replace the closing line with one observable next step?',
    'Your specifics are solid, but the closing phrasing reads as a judgment about the person rather than the work. What action could take its place?',
  ],
};

// Unified hash: FNV-style with Math.abs, byte-identical to the mockup's
// pickVariation. The previous DO frontend version was additive without
// Math.abs and could return undefined when the modulo went negative.
function pickVariation(key, text) {
  const variations = COACHING_VARIATIONS[key];
  if (!variations || variations.length === 0) return '';
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) {
    hash = ((hash << 5) - hash + text.charCodeAt(i)) | 0;
  }
  return variations[Math.abs(hash) % variations.length];
}

// Maps the rule-based engine's diagnoses (substance, structure, tone) to
// a coaching variation key. Returns null if no match -- caller falls back
// to generic_full.
function priorityToVariationKey(evidence) {
  const sub = evidence?.substance_diagnosis;
  const stru = evidence?.structure_diagnosis;
  const tone = evidence?.tone_diagnosis;
  // v6.3: wrap-up phrasing concern takes priority -- body is typically
  // constructive, the closing phrasing is the actionable target.
  if (tone === 'problematic_wrap_up') return 'problematic_wrap_up';
  if (sub === 'too_brief') return 'too_brief';
  if (sub === 'trait_only' || sub === 'trait_heavy') {
    return (stru === 'no_structure' || stru === 'observation_only') ? 'trait_full' : 'trait_partial';
  }
  if (sub === 'mixed_trait_behavior') {
    return (stru === 'observation_only' || stru === 'missing_next_steps') ? 'mixed_observation' : 'mixed_general';
  }
  if (sub === 'brief_but_process') return 'brief_process';
  if (sub === 'shallow' || sub === 'generic') {
    return (stru === 'observation_only' || stru === 'missing_next_steps') ? 'generic_observation' : 'generic_full';
  }
  if (sub === 'has_specifics_could_expand') return 'expand_specifics';
  if (sub === 'task_level_could_deepen') {
    return (stru === 'observation_only' || stru === 'missing_next_steps') ? 'deepen_task_structure' : 'deepen_task';
  }
  if (sub === 'adequate') {
    if (stru === 'observation_only') return 'adequate_observation';
    if (stru === 'missing_next_steps' || stru === 'vague_suggestion') return 'adequate_next_steps';
    if (tone === 'critical_only') return 'critical_only';
    if (tone === 'positive_only') return 'positive_only';
  }
  return null;
}

/* =====================================================================
 * Language detection (lifted from mockup's itp_scoring.js detectLanguage).
 * Module-local so the engine doesn't need to know about coaching languages.
 * ===================================================================== */
function detectLanguage(text) {
  if (typeof text !== 'string' || !text) return 'en';
  const textLower = text.toLowerCase();
  const tokens = {};
  const matches = textLower.match(/[a-zA-Z]+/g) || [];
  for (const t of matches) tokens[t] = true;
  const frMarkers = ['vous', 'nous', 'avec', 'dans', 'pour', 'aussi', 'notre', 'cette', 'sont', 'les', 'des', 'pas', 'qui', 'que', 'bon', 'travail', 'equipe', 'tu', 'ton'];
  const ptMarkers = ['voce', 'nos', 'com', 'para', 'tambem', 'muito', 'sempre', 'seus', 'suas', 'pelo', 'pela', 'nao', 'trabalho', 'equipe', 'equipa', 'projeto', 'bem', 'tiveste', 'fizeste', 'melhorar', 'otimo', 'colega', 'disposto', 'ajudar', 'mesmo', 'tarefas', 'bom', 'boa', 'foi', 'tem', 'ela', 'ele'];
  let frCount = 0; let ptCount = 0;
  for (const m of frMarkers) if (tokens[m]) frCount += 1;
  for (const m of ptMarkers) if (tokens[m]) ptCount += 1;
  if (frCount >= 2 && frCount > ptCount) return 'fr';
  if (ptCount >= 2 && ptCount > frCount) return 'pt';
  return 'en';
}

/* =====================================================================
 * French coaching path (lifted from mockup's generateFeedbackFr).
 * ===================================================================== */
function generateFeedbackFr(assessment) {
  const routing = assessment?.routing_label;
  if (assessment?.safety?.level === 0 || assessment?.safety?.mandatory_rephrase) {
    return "Votre commentaire doit etre reformule avant d'etre partage. Les commentaires doivent etre respectueux et constructifs.";
  }
  if (routing === 'ready_to_send' && assessment?.safety?.level === 2) {
    return 'Votre commentaire est bien redige et pret a etre soumis.';
  }
  const parts = [];
  if (routing === 'ready_to_send') parts.push('Votre commentaire est bien redige!');
  else if (routing === 'needs_minor_revision') parts.push('Votre commentaire est sur la bonne voie. Une amelioration le rendrait plus efficace:');
  else if (routing === 'needs_structural_revision') parts.push('Votre commentaire pourrait etre plus efficace avec quelques changements:');
  else parts.push('Votre commentaire necessite plus de developpement:');

  const evidence = assessment?.evidence || {};
  const issuesFr = {
    too_brief: "Decrivez une action precise, son importance pour l'equipe, et ce que votre coequipier devrait faire ensuite.",
    trait_only: 'Decrivez des actions specifiques plutot que des traits de personnalite.',
    trait_heavy: 'Decrivez des actions specifiques plutot que des traits de personnalite.',
    task_level_could_deepen: 'Decrivez comment la personne a aborde le travail, pas seulement ce qu\'elle a fait.',
    observation_only: 'Expliquez comment leurs actions ont affecte l\'equipe ou le projet.',
    missing_next_steps: 'Suggerez ce qu\'ils devraient faire ensuite.',
    vague_suggestion: 'Soyez plus precis dans votre suggestion: que devrait-il faire exactement?',
    critical_only: 'Commencez par quelque chose de positif, puis formulez vos preoccupations de maniere constructive.',
    positive_only: 'Ajoutez un domaine ou la personne pourrait s\'ameliorer.',
  };
  const lines = [];
  if (issuesFr[evidence.substance_diagnosis]) lines.push(issuesFr[evidence.substance_diagnosis]);
  if (issuesFr[evidence.structure_diagnosis] && !lines.includes(issuesFr[evidence.structure_diagnosis])) lines.push(issuesFr[evidence.structure_diagnosis]);
  if (issuesFr[evidence.tone_diagnosis] && !lines.includes(issuesFr[evidence.tone_diagnosis])) lines.push(issuesFr[evidence.tone_diagnosis]);
  for (const l of lines.slice(0, 2)) parts.push(`- ${l}`);
  return parts.join('\n');
}

/* =====================================================================
 * Portuguese coaching path (lifted from mockup's generateFeedbackPt).
 * ===================================================================== */
function generateFeedbackPt(assessment) {
  const routing = assessment?.routing_label;
  if (assessment?.safety?.level === 0 || assessment?.safety?.mandatory_rephrase) {
    return 'Seu comentario precisa ser reformulado antes de ser compartilhado. Os comentarios devem ser respeitosos e construtivos.';
  }
  if (routing === 'ready_to_send' && assessment?.safety?.level === 2) {
    return 'Seu comentario esta bem escrito e pronto para ser enviado.';
  }
  const parts = [];
  if (routing === 'ready_to_send') parts.push('Seu comentario esta bem escrito!');
  else if (routing === 'needs_minor_revision') parts.push('Seu comentario esta no caminho certo. Uma melhoria o tornaria mais forte:');
  else if (routing === 'needs_structural_revision') parts.push('Seu comentario poderia ser mais eficaz com algumas mudancas:');
  else parts.push('Seu comentario precisa de mais desenvolvimento:');

  const evidence = assessment?.evidence || {};
  const issuesPt = {
    too_brief: 'Descreva uma acao especifica, por que ela importou para a equipe, e o que seu colega deve fazer em seguida.',
    trait_only: 'Descreva acoes especificas em vez de tracos de personalidade.',
    trait_heavy: 'Descreva acoes especificas em vez de tracos de personalidade.',
    task_level_could_deepen: 'Descreva como a pessoa abordou o trabalho, nao apenas o que fez.',
    observation_only: 'Explique como as acoes afetaram a equipe ou o projeto.',
    missing_next_steps: 'Sugira o que eles devem fazer a seguir.',
    vague_suggestion: 'Seja mais especifico na sua sugestao: o que exatamente devem fazer?',
    critical_only: 'Comece com algo positivo e depois formule suas preocupacoes de forma construtiva.',
    positive_only: 'Adicione uma area onde a pessoa poderia melhorar.',
  };
  const lines = [];
  if (issuesPt[evidence.substance_diagnosis]) lines.push(issuesPt[evidence.substance_diagnosis]);
  if (issuesPt[evidence.structure_diagnosis] && !lines.includes(issuesPt[evidence.structure_diagnosis])) lines.push(issuesPt[evidence.structure_diagnosis]);
  if (issuesPt[evidence.tone_diagnosis] && !lines.includes(issuesPt[evidence.tone_diagnosis])) lines.push(issuesPt[evidence.tone_diagnosis]);
  for (const l of lines.slice(0, 2)) parts.push(`- ${l}`);
  return parts.join('\n');
}

/* =====================================================================
 * Main entry point.
 *
 * Order of decisions:
 *   1. Language gate -- French / Portuguese branches handle their own
 *      routing + diagnosis logic.
 *   2. Safety-critical -- safety.level === 0 returns a dedicated
 *      rephrase-required message (NOT normal coaching).
 *   3. Ready-to-send + clear safety -- positive static "ready to submit"
 *      message. (FIX #2 from the rule-based audit; the previous version
 *      fell through to generic_full and coached strong feedback as weak.)
 *   4. Routing-based opening + diagnosis-based variation.
 * ===================================================================== */
export function buildCoaching(assessment) {
  const text = assessment?.text || '';
  const language = detectLanguage(text);
  if (language === 'fr') return generateFeedbackFr(assessment);
  if (language === 'pt') return generateFeedbackPt(assessment);

  // English path below.
  const evidence = assessment?.evidence || {};
  const routing = assessment?.routing_label;
  const safetyLevel = assessment?.safety?.level;
  const mandatoryRephrase = assessment?.safety?.mandatory_rephrase;

  // Safety-critical mode: dedicated response, never normal coaching.
  if (safetyLevel === 0 || mandatoryRephrase) {
    return 'This feedback cannot be shared because it contains language that is inappropriate in a peer feedback setting. '
      + 'Please rewrite your feedback entirely. Effective peer feedback describes specific actions your teammate took, '
      + 'why those actions mattered, and what they should do next, all in a respectful and professional tone.';
  }

  // Ready-to-send + clear safety: positive static submit message.
  if (routing === 'ready_to_send' && safetyLevel === 2) {
    return 'Your feedback is well-formed and ready to submit.';
  }

  // Routing-keyed opening.
  let opening;
  if (routing === 'ready_to_send') {
    // Strong but cautionary (safety level 1) -- still give a strong-opening line.
    opening = 'Your feedback is strong. One optional improvement:';
  } else if (routing === 'needs_minor_revision') {
    opening = 'Your feedback is on the right track. A small improvement would make it stronger:';
  } else if (routing === 'needs_structural_revision') {
    opening = 'Your feedback could be more effective with some changes:';
  } else {
    opening = 'Your feedback needs more development to be helpful:';
  }

  // Pick the variation that best fits the diagnosis pattern.
  const key = priorityToVariationKey(evidence) || 'generic_full';
  const body = pickVariation(key, text);
  return body ? `${opening}\n${body}` : opening;
}
